'use client';

import NextLink, { LinkProps as NextLinkProps } from 'next/link';
import { HTMLAttributes, MouseEvent, forwardRef } from 'react';

export type LinkProps = NextLinkProps & HTMLAttributes<HTMLAnchorElement>;

const externalLinkPrefix = ['http', 'mailto', 'tel'];

/**
 * Next.js only scrolls to a hash on a pure hash-change navigation. It skips the scroll
 * when the search params change in the same transition (e.g. the `_storyblok` params in
 * draft mode get dropped) and when the URL does not change at all (clicking the same
 * anchor link twice). Scroll manually whenever the hash targets the current page.
 */
const scrollToHashOnSamePage = (href: NextLinkProps['href']) => {
  if (typeof href !== 'string') {
    return;
  }

  const [pathWithQuery, hash] = href.split('#');

  if (!hash) {
    return;
  }

  const path = pathWithQuery.split('?')[0];

  if (path && path !== window.location.pathname) {
    return;
  }

  document.getElementById(hash)?.scrollIntoView();
};

export const Link = forwardRef<HTMLAnchorElement, LinkProps>(
  ({ children, href, onClick, ...rest }, ref) => {
    const isExternalLink =
      typeof href === 'string' && externalLinkPrefix.some(prefix => href.startsWith(prefix));

    if (isExternalLink) {
      return (
        <a ref={ref} href={href} onClick={onClick} {...rest}>
          {children}
        </a>
      );
    }

    const handleClick = (event: MouseEvent<HTMLAnchorElement>) => {
      onClick?.(event);

      const isPlainLeftClick =
        event.button === 0 && !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey;

      if (event.defaultPrevented || !isPlainLeftClick) {
        return;
      }

      scrollToHashOnSamePage(href);
    };

    return (
      <NextLink ref={ref} href={href} onClick={handleClick} {...rest}>
        {children}
      </NextLink>
    );
  },
);

Link.displayName = 'Link';
